import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import InputField from '../../components/inputFields/InputField';
import ImagePickerField from '../../components/inputFields/ImagePickerField';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import { createBlogCategory, updateBlogCategory, getBlogCategory } from '../../services/blogCategory';

const BlogCategoryFormPage = ({ onSuccess, onCancel, hideHeader = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [initialValues, setInitialValues] = useState({
        name: '',
        description: '',
        color: '#10B981',
        order: 0,
        is_active: true
    });

    const validationSchema = Yup.object({
        name: Yup.string()
            .required('Category name is required')
            .min(2, 'Category name must be at least 2 characters')
            .max(255, 'Category name must be less than 255 characters'),
        description: Yup.string()
            .max(1000, 'Description must be less than 1000 characters'),
        color: Yup.string()
            .matches(/^#[0-9A-F]{6}$/i, 'Color must be a valid hex color code'),
        order: Yup.number()
            .min(0, 'Order must be a positive number')
            .integer('Order must be a whole number'),
        is_active: Yup.boolean()
    });


    useEffect(() => {
        if (isEdit && id) {
            fetchCategoryData();
        }
    }, [isEdit, id]);

    const fetchCategoryData = async () => {
        try {
            setInitialLoading(true);
            const response = await getBlogCategory(id);
            if (response.status === 200) {
                const data = response.data;
                setPreviewImage(data.icon);
                setInitialValues({
                    name: data.name || '',
                    description: data.description || '',
                    color: data.color || '#10B981',
                    order: data.order || 0,
                    is_active: data.is_active !== undefined ? data.is_active : true
                });
            }
        } catch (error) {
            console.error('Error fetching category:', error);
            toast.error('Failed to fetch category data');
            if (onCancel) {
                onCancel();
            } else {
                navigate('/blog/categories');
            }
        } finally {
            setInitialLoading(false);
        }
    };

    const handleImageSelect = (file) => {
        setImageFile(file);
        setPreviewImage(URL.createObjectURL(file));
    };

    const handleImageClear = () => {
        setImageFile(null);
        setPreviewImage(null);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setLoading(true);

            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('description', values.description || '');
            formData.append('color', values.color);
            formData.append('order', values.order);
            formData.append('is_active', values.is_active);

            if (imageFile) {
                formData.append('icon', imageFile);
            }

            let response;
            if (isEdit) {
                response = await updateBlogCategory({
                    id: id,
                    data: formData
                });
            } else {
                response = await createBlogCategory(formData);
            }

            if (response.status === 200 || response.status === 201) {
                toast.success(`Category ${isEdit ? 'updated' : 'created'} successfully`);
                if (onSuccess) {
                    onSuccess(response.data);
                } else {
                    navigate('/blog/categories');
                }
            } else {
                toast.error(`Failed to ${isEdit ? 'update' : 'create'} category`);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error(`Failed to ${isEdit ? 'update' : 'create'} category`);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const handleCancelClick = () => {
        if (onCancel) {
            onCancel();
        } else {
            navigate('/blog/categories');
        }
    };

    if (initialLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <CustomLoading size={100} />
            </div>
        );
    }

    return (
        <>
            {!hideHeader && (
                <>
                    <Breadcrumbs items={[
                        { name: 'Home', link: '/dashboard' },
                        { name: 'Blog', link: '/blog/dashboard' },
                        { name: 'Categories', link: '/blog/categories' },
                        { name: isEdit ? 'Edit Category' : 'Create Category' },
                    ]} />

                    <div className="py-3">
                        <div className="mb-6">
                            <StyledHeadingText text={isEdit ? 'Edit Category' : 'Create New Category'} />
                            <p className="text-sm text-gray-600 mt-1">
                                {isEdit ? 'Update category information' : 'Add a new blog category'}
                            </p>
                        </div>
                    </div>
                </>
            )}

            <div className={`${!hideHeader ? 'bg-white rounded-lg shadow-sm border border-gray-200 p-6' : ''}`}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                    key={isEdit ? `edit-${id}` : 'create'}
                >
                    {({ values, setFieldValue, isSubmitting }) => (
                        <Form className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <InputField
                                        name="name"
                                        label="Category Name"
                                        placeholder="Enter category name"
                                        required
                                    />
                                </div>
                                <div>
                                    <InputField
                                        name="color"
                                        label="Color"
                                        placeholder="#10B981"
                                        type="color"
                                    />
                                </div>
                            </div>

                            <div>
                                <InputField
                                    name="description"
                                    label="Description"
                                    placeholder="Enter category description (optional)"
                                    type="textarea"
                                    rows={3}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <InputField
                                        name="order"
                                        label="Order"
                                        type="number"
                                        placeholder="0"
                                        min="0"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <InputField
                                        name="is_active"
                                        label="Active"
                                        type="checkbox"
                                    />
                                </div>
                            </div>

                            <div>
                                <ImagePickerField
                                    label="Category Icon"
                                    preloadedImage={previewImage}
                                    onImageSelect={handleImageSelect}
                                    onClear={handleImageClear}
                                    placeholder="Upload category icon"
                                    acceptedFormats=".jpg,.png,.jpeg,.svg"
                                    maxFileSize={5}
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <Button
                                    type="button"
                                    onClick={handleCancelClick}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting || loading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : (isEdit ? 'Update Category' : 'Create Category')}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default BlogCategoryFormPage;
